import { CheckIcon, XIcon } from '@heroicons/react/outline';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { updateCategory, UpdateCategoryPayload } from 'api/category';
import ReactSelect from 'components/UI/Form/ReactSelect';
import LoadingTable from 'components/UI/loading/LoadingTable';
import Table from 'components/UI/Table';
import { ReactQueryKey } from 'constants/ReactQueryKeys';
import {
  useCategories,
  useCategoryGroup,
  useCategorySubGroup,
} from 'hooks/ReactQueryHooks/useCategoryQuery';
import { notifyUser } from 'store/Toast';
import { CategoriesGroup, CategoriesParams } from 'types/Category';
import { QueryParamsInterface } from 'types/Shared';
import { isObjectEmpty, sanitizeResponseError } from 'utils';
import EditableCell from './EditableCell';
import Input from 'components/UI/Form/Input';
import EditIcon from 'components/UI/Icons/EditIcon';

interface CategoriesTableProps {
  filterData: any;
}

export const CategoriesList = ({ filterData }: CategoriesTableProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [params, setParams] = useState<CategoriesParams>({
    perPage: 10,
    page: 1,
  });
  const [data, setData] = useState();
  const [touchedInput, setTouchedInput] = useState<{
    [key: string]: boolean;
  }>();
  // const [parentCategory, setParentCategory] = useState<Category | null>(null);
  const [parentCategoryGroup, setParentCategoryGroup] =
    useState<CategoriesGroup>({ name: 'Task', id: 89 });
  const [skipPageReset, setSkipPageReset] = useState(false);
  const categoryNameRef = useRef<HTMLInputElement | null>(null);
  const categoryDescriptionRef = useRef<HTMLInputElement | null>(null);
  const [inputError, setInputError] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    setParams((prevState) => ({ ...prevState, ...filterData }));
  }, [filterData]);

  const fetchCategories = useCategories({
    onSuccess: (d: any) => setData(d.data),
    params: params as QueryParamsInterface,
  });
  const categories = fetchCategories?.data?.data;
  const categoriesCount = fetchCategories?.data?.meta?.count;

  const fetchCategoriesGroup = useCategoryGroup({});
  const categoriesGroup = fetchCategoriesGroup?.data?.data;

  const fetchCategoriesSubGroup = useCategorySubGroup({
    group: parentCategoryGroup,
  });
  const categoriesSubGroup = fetchCategoriesSubGroup?.data?.data;

  const { perPage = 10, page = 1 } = params;
  const pageCount = Math.ceil(Number(categoriesCount) / perPage);

  const patchUpdatedCategory = useMutation(
    (data: { id: number; payload: Partial<UpdateCategoryPayload> }) =>
      updateCategory({ id: data.id, data: data.payload }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKey.GET_CATEGORIES]);
        setTouchedInput({});
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Category saved successfully.`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        let resError = sanitizeResponseError(error);
        cancelUpdate();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const nextPage = () => {
    const nextPage = page + 1;
    if (nextPage <= pageCount) {
      setParams((prev) => ({ ...prev, page: nextPage }));
    }
  };
  const previousPage = () => {
    const previousPage = page - 1;
    if (previousPage > 0) {
      setParams((prev) => ({ ...prev, page: previousPage }));
    }
  };

  const updateTableData = (
    rowIndex: number,
    columnId: number,
    value: string | CategoriesGroup
  ) => {
    // We also turn on the flag to not reset the page
    if (columnId.toString() !== 'name') {
      setTouchedInput((prevState) => ({
        ...prevState,
        [columnId]: true,
      }));
    }

    setSkipPageReset(true);
    setData((old: any) =>
      old.map((row: any, index: any) => {
        if (index === rowIndex) {
          if (
            columnId.toString() === 'name' &&
            old[rowIndex][columnId]?.trim() !== value?.toString()?.trim()
          ) {
            setTouchedInput((prevState) => ({
              ...prevState,
              [columnId]: true,
            }));
          }
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const cancelUpdate = () => {
    setData(categories);
  };

  const tableColumns = [
    {
      id: 'name',
      Header: 'Name',
      accessor: 'name',
      minWidth: 250,
      maxWidth: 250,
      Cell: (row: any) => {
        const category = row?.row?.original;
        if (row.editableRowIndex !== row.row.index) {
          return <p className='truncate'>{row.value}</p>;
        } else {
          return (
            <div className='relative'>
              <Input
                ref={categoryNameRef}
                type='text'
                name={category.id}
                defaultValue={category.name}
                placeholder='e.g. Office building template'
                maxLength={50}
                onBlur={() => {
                  if (categoryNameRef?.current?.value) {
                    updateTableData(
                      row.row.index,
                      row.column.id,
                      categoryNameRef.current.value
                    );
                    setInputError((prevState) => ({
                      ...prevState,
                      name: '',
                    }));
                  } else {
                    updateTableData(row.row.index, row.column.id, '');
                    setInputError((prevState) => ({
                      ...prevState,
                      name: 'This is required',
                    }));
                  }
                }}
                error={inputError.name}
              />
              <span className='text-xs absolute'>50 Characters</span>
            </div>
          );
        }
      },
    },
    {
      id: 'group',
      Header: 'Group',
      accessor: 'parentGroup',
      minWidth: 250,
      maxWidth: 250,
      Cell: (row: any) => {
        if (row.editableRowIndex !== row.row.index) {
          return <div className='capitalize'>{row.value.name}</div>;
        } else {
          return (
            <ReactSelect
              id='categoryGroup'
              name='categoryGroup'
              defaultValue={{ name: row.value.name, id: row.value.id }}
              options={categoriesGroup}
              getOptionLabel={(option) => (option as CategoriesGroup).name}
              getOptionValue={(option) =>
                (option as CategoriesGroup).id.toString()
              }
              onChange={(option) => {
                setParentCategoryGroup(option as CategoriesGroup);
                updateTableData(
                  row.row.index,
                  row.column.id,
                  option as CategoriesGroup
                );
              }}
            />
          );
        }
      },
    },
    {
      id: 'subGroup',
      Header: 'Sub Group',
      accessor: 'subGroup',
      minWidth: 250,
      maxWidth: 250,
      Cell: (row: any) => {
        if (row.editableRowIndex !== row.row.index) {
          return (
            <div className='capitalize'>{row.value ? row.value.name : '-'}</div>
          );
        } else {
          return (
            <ReactSelect
              id='categorySubGroup'
              name='categorySubGroup'
              defaultValue={
                row.value && { name: row.value.name, id: row.value.id }
              }
              options={categoriesSubGroup}
              getOptionLabel={(option) => (option as CategoriesGroup).name}
              getOptionValue={(option) =>
                (option as CategoriesGroup).id.toString()
              }
              onChange={(option) =>
                updateTableData(
                  row.row.index,
                  row.column.id,
                  option as CategoriesGroup
                )
              }
            />
          );
        }
      },
    },
    {
      id: 'description',
      Header: 'Description',
      accessor: 'description',
      minWidth: 250,
      maxWidth: 250,
      Cell: (row: any) => {
        const category = row?.row?.original;
        if (row.editableRowIndex !== row.row.index) {
          return <p className='break-words line-clamp-2'>{row.value}</p>;
        } else {
          return (
            <div className='relative'>
              <Input
                ref={categoryDescriptionRef}
                type='text'
                name={category.id}
                defaultValue={category.description}
                customClassName=''
                maxLength={250}
                onBlur={() => {
                  if (categoryDescriptionRef?.current?.value) {
                    updateTableData(
                      row.row.index,
                      row.column.id,
                      categoryDescriptionRef.current.value
                    );
                    setInputError((prevState) => ({
                      ...prevState,
                      description: '',
                    }));
                  } else {
                    updateTableData(row.row.index, row.column.id, '');
                    setInputError((prevState) => ({
                      ...prevState,
                      description: 'This is required',
                    }));
                  }
                }}
                error={inputError.description}
              />
              <span className='text-xs absolute'>250 Characters</span>
            </div>
          );
        }
      },
    },
    {
      id: 'edit',
      Header: '',
      accessor: 'edit',
      width: 50,
      Cell: (row: any) => {
        if (row.editableRowIndex !== row.row.index) {
          return (
            <div
              className='flex justify-center'
              onClick={() => {
                cancelUpdate();
                setParentCategoryGroup(row?.row?.original?.parentGroup);
                row.setEditableRowIndex(row.row.index);
              }}
            >
              <EditIcon />
            </div>
            // <SimpleDropDown
            //   menuButtonClassName="flex justify-center w-full"
            //   menuItemClassName="lg:right-12 z-10 text-xs"
            //   toggle={
            //     <div className="text-neutralColor-normal hover:bg-neutralColor-lighter rounded-full p-1 transition duration-300 ease-in-out">
            //       <DotsVerticalIcon className="h-5 w-5" />
            //     </div>
            //   }
            // >
            //   <DropDownItem
            //     id="addSubCategory"
            //     onClick={() => {
            //       modalHandler("newSubCategory");
            //       setParentCategory(row?.row?.original);
            //     }}
            //   >
            //     <div className="flex items-center gap-2 text-xs">
            //       <PlusIcon className="h-5 w-5" />
            //       <span>Add Sub Category</span>
            //     </div>
            //   </DropDownItem>
            //   <DropDownItem
            //     id="editCategory"
            //     onClick={() => {
            //       cancelUpdate();
            //       row.setEditableRowIndex(row.row.index);
            //     }}
            //   >
            //     <div className="flex items-center gap-2 text-xs">
            //       <PencilAltIcon className="h-5 w-5" />
            //       <span>Edit</span>
            //     </div>
            //   </DropDownItem>
            //   <DropDownItem id="deleteCategory" onClick={() => {}}>
            //     <div className="flex items-center gap-2 text-xs">
            //       <TrashIcon className="h-5 w-5" />
            //       <span>Delete</span>
            //     </div>
            //   </DropDownItem>
            // </SimpleDropDown>
          );
        } else {
          return (
            <div className='flex justify-center gap-4'>
              <span
                className='text-primaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-1 transition duration-300 ease-in-out'
                onClick={() => {
                  row.setEditableRowIndex(null);
                  handleSave({
                    ...row.row.values,
                    id: row.row.original.id,
                  });
                }}
              >
                <CheckIcon className='h-4 w-4' />
              </span>
              <span
                className='text-tertiaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-1 transition duration-300 ease-in-out'
                onClick={() => {
                  row.setEditableRowIndex(null);
                  cancelUpdate();
                }}
              >
                <XIcon className='h-4 w-4' />
              </span>
            </div>
          );
        }
      },
    },
  ];

  const defaultColumn = {
    Cell: EditableCell,
  };

  const handleSave = (updatedData: any) => {
    let categoryTypeId = updatedData.group.id;
    if (updatedData.subGroup) {
      categoryTypeId = updatedData.subGroup.id;
    }

    const updatedCategoryData: Partial<UpdateCategoryPayload> = {
      ...(touchedInput?.name && { name: updatedData.name }),
      ...(touchedInput?.description && {
        description: updatedData.description,
      }),
      ...((touchedInput?.subGroup || touchedInput?.group) && {
        categoryTypeId,
      }),
    };

    if (!isObjectEmpty(updatedCategoryData)) {
      patchUpdatedCategory.mutate({
        id: updatedData.id,
        payload: updatedCategoryData,
      });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `No changes were detected to save the category.`,
          toastType: 'info',
        })
      );
    }
  };

  const categoriesTablePagination = {
    pageCount: pageCount,
    pageSize: perPage,
    currentPage: page,
    page,
    setPageSize: (pageSize: number) =>
      setParams((params) => ({ ...params, perPage: pageSize })),
    nextPage,
    previousPage,
  };

  return (
    <>
      <div className='flex my-4 gap-4 items-center'>
        <div className='h-full w-full'>
          {data && (
            <Table
              cols={tableColumns}
              defaultColumn={defaultColumn}
              tableData={data}
              updateTableData={updateTableData}
              fetching={fetchCategories.isLoading}
              pagination={categoriesTablePagination}
            />
          )}
          {!data && <LoadingTable />}
        </div>
      </div>
    </>
  );
};
